import { useEffect, useState } from "react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Sparkles, Lock, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function HostSubscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/host/subscription"); setSub(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setActivating(true);
    try {
      await api.post("/host/subscription/activate");
      toast.success("Subscription activated! Auto-pay set up.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setActivating(false); }
  };

  if (loading) return <div className="py-20 text-center text-[#717171]">Loading...</div>;

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-display font-bold mb-2">Host subscription</h1>
        <p className="text-[#717171] mb-8">Unlock direct inquiries, contact reveals & custom-price offers</p>

        <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#FF385C]/10 flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#FF385C]" />
              </div>
              <div>
                <p className="font-display font-semibold text-lg">Current plan</p>
                <Badge data-testid="subscription-status-badge" className={
                  sub.status === "active" ? "bg-[#008A05]/10 text-[#008A05] border-0" :
                  sub.status === "trial" ? "bg-[#FFB400]/15 text-[#A07000] border-0" :
                  "bg-[#C13515]/10 text-[#C13515] border-0"
                }>
                  {sub.status === "trial" ? `Free trial · ${sub.trial_days_left} days left` :
                   sub.status === "active" ? "Active · auto-renews monthly" :
                   "Expired"}
                </Badge>
              </div>
            </div>
            {sub.status !== "active" && (
              <Button data-testid="activate-subscription-btn" onClick={activate} disabled={activating} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">
                {activating ? "Activating..." : `Subscribe ${inr(sub.monthly_fee)}/mo`}
              </Button>
            )}
          </div>
          {sub.status === "active" && sub.next_charge_at && (
            <p className="text-sm text-[#717171] mt-4 flex items-center gap-1">
              <Calendar className="w-4 h-4" /> Next charge: {new Date(sub.next_charge_at).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-2xl p-8">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-display font-bold text-4xl">{inr(sub.monthly_fee)}</span>
            <span className="text-[#717171]">/ month</span>
            <span className="ml-auto text-xs text-[#008A05] font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" />First month free</span>
          </div>
          <p className="text-[#717171] text-sm mb-6">Everything you need to run a direct-booking business</p>
          <ul className="space-y-3 text-sm">
            {[
              "Receive unlimited direct booking inquiries",
              "Reveal traveler phone & email instantly",
              "Send custom-price offers via the app",
              "Accept WhatsApp + call bookings",
              "Zero booking commission — keep 100% of room revenue",
              "Auto-renew via Razorpay; cancel anytime",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-[#008A05]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-[#008A05]" strokeWidth={3} />
                </div>
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl bg-[#F7F7F9] p-4 text-xs text-[#555] flex gap-2">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" /> Travelers still pay a ₹299 per-booking platform fee — this is separate from your monthly host subscription.
          </div>
        </div>
      </div>
    </div>
  );
}
